import { Spinner } from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import NegativeComparisonIndicator from "../assets/NegativeComparisonIndicator.svg"
import PositiveComparisonIndicator from "../assets/PositiveComparisonIndicator.svg"
import { TopMenu } from "../components/TopMenu.jsx"
import { KopyProtectUpgradeCards } from "../components/protect/KopyProtectUpgradeCards.jsx"
import { ProtectFeatureCard } from "../components/protect/ProtectFeatureCard.jsx"
import { ProtectGlobalSettings } from "../components/protect/ProtectGlobalSettings.jsx"
import { ScriptTagGuide } from "../components/protect/ScriptTagGuide.jsx"
import { UpgradeProtectModal } from "../components/protect/UpgradeProtectModal.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { ProtectStateContext } from "../components/providers/ProtectStateProvider.jsx"
import {
	useCheckScriptTag,
	useGetFeatureSettings,
	useUpdateFeatureSetting,
} from "../hooks/useProtectHooks.js"
import {
	protectFeatures,
	protectFeaturesArr,
} from "../utils/protectFeatures.jsx"

const Protect = () => {
	const { state } = useContext(GlobalStateContext)
	const { protectState, setProtectState } = useContext(ProtectStateContext)
	const getFeatureSettings = useGetFeatureSettings()
	const updateFeatureSetting = useUpdateFeatureSetting()
	const checkScriptTag = useCheckScriptTag()

	const navigate = useNavigate()
	const { t } = useTranslation()

	const [loading, setLoading] = useState(true)
	const [scriptTagIsPresent, setScriptTagIsPresent] = useState(false)

	const checkScriptTagStatus = async () => {
		const scriptTagIsPresent = await checkScriptTag()

		setScriptTagIsPresent(scriptTagIsPresent)
	}

	const getProtectSettings = async () => {
		const fetchedFeatureSettings = await getFeatureSettings()
		const featureSettings = {}
		let globalConfig = null

		if (fetchedFeatureSettings) {
			if (fetchedFeatureSettings?.global_config) {
				globalConfig = fetchedFeatureSettings.global_config
				delete fetchedFeatureSettings.global_config
			}

			for (const protectFeature in protectFeatures) {
				if (fetchedFeatureSettings?.[protectFeature]) {
					featureSettings[protectFeature] =
						fetchedFeatureSettings[protectFeature]
				} else {
					featureSettings[protectFeature] =
						protectFeatures[protectFeature].defaultValue
				}
			}
		} else {
			for (const protectFeature in protectFeatures) {
				featureSettings[protectFeature] =
					protectFeatures[protectFeature].defaultValue
			}
		}

		setProtectState({
			...protectState,
			featureSettings,
			globalSettings: globalConfig,
		})
	}

	const toggleAllSettings = (enabled) => {
		const newFeatureSettings = {}

		for (const featureSetting in protectState.featureSettings) {
			if (
				typeof protectState.featureSettings[featureSetting] ===
				"boolean"
			) {
				newFeatureSettings[featureSetting] = enabled
			} else {
				newFeatureSettings[featureSetting] = {
					...newFeatureSettings[featureSetting],
					enabled,
				}
			}
		}

		setProtectState({
			...protectState,
			featureSettings: newFeatureSettings,
		})

		updateFeatureSetting(newFeatureSettings)
	}

	useEffect(async () => {
		if (state.hasValidKopyProtectSubscription) {
			await checkScriptTagStatus()
			await getProtectSettings()
			setLoading(false)
		} else {
			navigate("/upgradeprotect")
		}
	}, [state.kopyProtectSubscription])

	return (
		<div>
			<TopMenu goBack="/" storeSelector />

			<div
				style={{
					padding: "2rem",
					paddingTop: "8rem",
					maxWidth: "1030px",
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				{loading ? (
					<div
						style={{
							display: "flex",
							width: "100%",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Spinner />
					</div>
				) : (
					<>
						{scriptTagIsPresent ? (
							<h1
								style={{
									fontSize: "4rem",
									fontWeight: "800",
									marginBottom: "4rem",
									textAlign: "center",
								}}
							>
								<svg
									style={{
										height: "3.5rem",
										width: "3.5rem",
										color: "#222",
										marginRight: "1rem",
									}}
									viewBox="0 0 512 512"
								>
									<path
										fill="currentColor"
										d="M256 0c4.6 0 9.2 1 13.4 2.9l188.3 79.9c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0m0 66.8v378C394 378 431.1 230.1 432 141.4z"
									></path>
								</svg>
								<span>Kopy Protect</span>
							</h1>
						) : (
							<ScriptTagGuide
								onScriptTagCreate={() =>
									setScriptTagIsPresent(true)
								}
							/>
						)}

						<div
							style={{
								pointerEvents: scriptTagIsPresent
									? "all"
									: "none",
								filter: scriptTagIsPresent
									? "none"
									: "blur(1.5px)",
								display: "flex",
								gap: "1rem",
								alignItems: "center",
								marginTop: "1rem",
								justifyContent: "space-between",
							}}
						>
							<div
								style={{
									display: "flex",
									gap: "1rem",
									pointerEvents:
										state.hasValidKopyProtectSubscription
											? "all"
											: "none",
								}}
							>
								<ProtectGlobalSettings />
							</div>

							<div style={{ display: "flex", gap: "1rem" }}>
								<button
									className="neu-background shadow neu-button"
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
									}}
									onClick={() => toggleAllSettings(true)}
								>
									<img
										style={{
											width: "16px",
											height: "16px",
										}}
										src={PositiveComparisonIndicator}
									/>
									<span
										style={{
											fontSize: "0.8rem",
											fontWeight: "600",
										}}
									>
										{t("protect.enable_all")}
									</span>
								</button>
								<button
									className="neu-background shadow neu-button"
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
									}}
									onClick={() => toggleAllSettings(false)}
								>
									<img
										style={{
											width: "16px",
											height: "16px",
										}}
										src={NegativeComparisonIndicator}
									/>
									<span
										style={{
											fontSize: "0.8rem",
											fontWeight: "600",
										}}
									>
										{t("protect.disable_all")}
									</span>
								</button>
							</div>
						</div>

						<div
							style={{
								pointerEvents: scriptTagIsPresent
									? "all"
									: "none",
								filter: scriptTagIsPresent
									? "none"
									: "blur(1.5px)",
								display: "flex",
								flexWrap: "wrap",
								gap: "1rem",
								marginTop: "1rem",
								marginBottom: "2rem",
								width: "auto",
								marginLeft: "auto",
								marginRight: "auto",
								justifyContent: "center",
							}}
						>
							{protectFeaturesArr.map((protectFeature) => {
								return (
									<ProtectFeatureCard
										key={protectFeature.id}
										settingId={protectFeature.id}
									/>
								)
							})}
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default Protect
